import ToolTipIcon from '@assets/images/common/icon/tooltip/tooltip.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { tenantRoleApiClient } from '@src/api/tenant/tenantRole/tenantRoleApiClient';
import {
  tenantUserApiClient,
  TenantUserCreateErrorResponse,
} from '@src/api/tenant/tenantUser/tenantUserApiClient';
import { useToast } from '@src/features/toast/context/toastContext';
import { useScrollToError } from '@src/hooks/useScrollToError';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button';
import ErrorMessage from '@src/ui/elements/form/ErrorMessage';
import Input from '@src/ui/elements/form/Input';
import Select, { SelectorItem } from '@src/ui/elements/form/Select';
import TenantRoleTooltip from '@src/ui/elements/form/TenantRoleTooltip';
import Title from '@src/ui/elements/typography/Title.tsx';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type FormValues = {
  familyName: string;
  firstName: string;
  familyNameKana: string;
  firstNameKana: string;
  email: string;
  tenantRoleId: string;
};

type RoleSelector = SelectorItem[];

const TenantUserCreateForm: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const helpIconRef = React.createRef<HTMLDivElement>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<TenantUserCreateErrorResponse>();
  const [roleSelector, setRoleSelector] = useState<RoleSelector>();
  const [formValues, setFormValues] = useState<FormValues>({
    familyName: '',
    firstName: '',
    familyNameKana: '',
    firstNameKana: '',
    email: '',
    tenantRoleId: '',
  });
  const [isTooltipActive, setIsTooltipActive] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const TOOLTIP_OFFSET_X = 10;
  const TOOLTIP_OFFSET_Y = 90;
  const TOOLTIP_ADJUST_Y = 150;
  const TOOLTIP_HEIGHT_THRESHOLD = 100;

  useEffect(() => {
    const fetchTenantRoles = async () => {
      const res = await tenantRoleApiClient.index();
      handleApiResponse(res, (data) => {
        setRoleSelector(
          data.map((role) => {
            return {
              id: role.id,
              name: role.name,
            };
          }),
        );
      });
    };
    fetchTenantRoles();
  }, []);

  const submit = async () => {
    if (submitted) {
      return;
    }
    setErrors(undefined);
    setSubmitted(true);
    const res = await tenantUserApiClient.create({
      tenant_user: {
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        email: formValues.email,
        role_id: formValues.tenantRoleId,
      },
    });

    handleApiResponse(
      res,
      () => {
        toast(TOAST_MESSAGE.REGISTER);
        navigate(TENANT_URL.TENANT_USERS);
      },
      (errors: TenantUserCreateErrorResponse) => {
        setErrors(errors);
        setSubmitted(false);
      },
    );
  };

  const handleClickCancel = () => {
    navigate(TENANT_URL.TENANT_USERS);
  };

  const fieldRefs = useScrollToError(errors ?? {});

  const calculateBasePosition = (rect: DOMRect) => ({
    x: rect.left + window.scrollX + rect.width + TOOLTIP_OFFSET_X,
    y: rect.top + window.scrollY - TOOLTIP_OFFSET_Y,
  });

  const adjustTooltipPosition = (basePosition: { x: number; y: number }, rect: DOMRect) => {
    if (rect.bottom + TOOLTIP_HEIGHT_THRESHOLD > window.innerHeight) {
      return {
        x: basePosition.x,
        y: basePosition.y - TOOLTIP_ADJUST_Y,
      };
    }
    return basePosition;
  };

  const handleTooltipPosition = () => {
    if (!helpIconRef.current) {
      return;
    }
    const rect = helpIconRef.current.getBoundingClientRect();
    const basePosition = calculateBasePosition(rect);
    const adjustedPosition = adjustTooltipPosition(basePosition, rect);
    setTooltipPosition(adjustedPosition);
  };

  const handleMouseOver = () => {
    setIsTooltipActive(true);
    handleTooltipPosition();
  };

  const handleMouseLeave = () => {
    setIsTooltipActive(false);
  };

  return (
    <main className='px-6'>
      <div>
        <div className='flex flex-col'>
          <div className='mt-12 flex justify-between gap-4'>
            <div className='pt-2'>
              <Title>テナントユーザーの作成</Title>
            </div>
          </div>
        </div>
        <div className='mx-auto max-w-[800px] py-[60px]'>
          <form
            className='mt-10 flex flex-col gap-[26px]'
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className=''>氏名</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex gap-4'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['tenant_user.family_name'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='山田'
                    onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                    error={hasError(errors?.tenant_user.family_name)}
                  />
                  <ErrorMessage>{firstError(errors?.tenant_user.family_name)}</ErrorMessage>
                </div>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['tenant_user.first_name'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='太郎'
                    onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                    error={hasError(errors?.tenant_user.first_name)}
                  />
                  <ErrorMessage>{firstError(errors?.tenant_user.first_name)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className=''>氏名（フリガナ）</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex gap-4'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['tenant_user.family_name_kana'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='ヤマダ'
                    onChange={(e) =>
                      setFormValues({ ...formValues, familyNameKana: e.target.value })
                    }
                    error={hasError(errors?.tenant_user.family_name_kana)}
                  />
                  <ErrorMessage>{firstError(errors?.tenant_user.family_name_kana)}</ErrorMessage>
                </div>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['tenant_user.first_name_kana'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='タロウ'
                    onChange={(e) =>
                      setFormValues({ ...formValues, firstNameKana: e.target.value })
                    }
                    error={hasError(errors?.tenant_user.first_name_kana)}
                  />
                  <ErrorMessage>{firstError(errors?.tenant_user.first_name_kana)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className=''>メールアドレス</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div
                className='flex'
                ref={(el) => (fieldRefs.current['tenant_user.email'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='sample@sample.co.jp'
                  onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
                  error={hasError(errors?.tenant_user.email)}
                />
              </div>
              <ErrorMessage>{firstError(errors?.tenant_user.email)}</ErrorMessage>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className=''>権限</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
                <div
                  ref={helpIconRef}
                  onMouseOver={handleMouseOver}
                  onMouseLeave={handleMouseLeave}
                >
                  <img
                    className=''
                    src={ToolTipIcon}
                    alt=''
                    width='15'
                    height='15'
                  />
                  <TenantRoleTooltip
                    active={isTooltipActive}
                    x={tooltipPosition.x}
                    y={tooltipPosition.y}
                  />
                </div>
              </div>
              {roleSelector && (
                <div
                  className='flex gap-4'
                  ref={(el) => (fieldRefs.current['tenant_user.role_id'] = el)}
                >
                  <div className='w-full [&>*]:w-full'>
                    <Select
                      items={roleSelector}
                      size='m'
                      onSelect={(value) => setFormValues({ ...formValues, tenantRoleId: value })}
                      error={hasError(errors?.tenant_user.role_id)}
                    />
                  </div>
                  <div className='w-full'></div>
                </div>
              )}
              <ErrorMessage>{firstError(errors?.tenant_user.role_id)}</ErrorMessage>
            </div>
            <div className='mt-auto flex justify-center gap-5'>
              <div className='w-[172px]'>
                <Button
                  variant='secondary'
                  fullRounded
                  fullWidth
                  outline
                  onClick={handleClickCancel}
                >
                  キャンセル
                </Button>
              </div>
              <div className='w-[172px]'>
                <Button
                  fullRounded
                  fullWidth
                  outline
                  onClick={submit}
                >
                  作成する
                </Button>
              </div>
            </div>
            <p className='grid place-items-center text-sm'>
              入力したメールアドレス宛に認証メールが送信されます
            </p>
          </form>
        </div>
      </div>
    </main>
  );
};

export default TenantUserCreateForm;
